'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CloseGlyph } from '../../../internal/icons.js';
import { useFieldControl } from '../../forms/form-field/index.js';
import { Spinner } from '../../feedback/spinner/index.js';
import { Kbd } from '../../typography/kbd/index.js';
function SearchGlyph() {
    return (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round", "aria-hidden": true, focusable: false, children: [_jsx("circle", { cx: "7", cy: "7", r: "4.5" }), _jsx("path", { d: "M10.5 10.5L14 14" })] }));
}
function isEditable(target) {
    if (!(target instanceof HTMLElement))
        return false;
    return (target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT');
}
/**
 * True when a keyboard event matches one `aria-keyshortcuts` combination such
 * as "Meta+K" or "/". Modifiers that are not named must not be held (Shift is
 * ignored for single printable keys, since it is how some symbols are typed).
 */
function matchesShortcut(event, combo) {
    const parts = combo.split('+').map((p) => p.trim());
    const key = parts.pop();
    if (!key)
        return false;
    const mods = new Set(parts.map((p) => p.toLowerCase()));
    const want = {
        ctrl: mods.has('control') || mods.has('ctrl'),
        meta: mods.has('meta'),
        alt: mods.has('alt'),
        shift: mods.has('shift'),
    };
    if (event.ctrlKey !== want.ctrl || event.metaKey !== want.meta || event.altKey !== want.alt)
        return false;
    if (want.shift && !event.shiftKey)
        return false;
    if (!want.shift && event.shiftKey && key.length > 1)
        return false;
    return event.key.toLowerCase() === key.toLowerCase();
}
/**
 * A search box: leading icon, clear button, optional debounce for fetching,
 * a pending state, and an optional keyboard shortcut that focuses it.
 * Renders a native `type="search"` input, so give it a visible label (Field)
 * or an `aria-label`.
 */
export const SearchInput = forwardRef(function SearchInput({ value: valueProp, defaultValue = '', onValueChange, onSearch, debounce = 0, pending = false, clearable = true, clearLabel = 'Clear search', onClear, shortcut, shortcutKeys, icon, size = 'md', invalid, className, onChange, onKeyDown, ...rest }, ref) {
    const [internal, setInternal] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : internal;
    const inputRef = useRef(null);
    const timer = useRef(null);
    const searchRef = useRef(onSearch);
    searchRef.current = onSearch;
    const cancel = useCallback(() => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);
    useEffect(() => cancel, [cancel]);
    const runSearch = (query, immediate) => {
        cancel();
        if (!searchRef.current)
            return;
        if (immediate || debounce <= 0)
            searchRef.current(query);
        else
            timer.current = setTimeout(() => {
                timer.current = null;
                searchRef.current?.(query);
            }, debounce);
    };
    const setValue = (next) => {
        if (valueProp === undefined)
            setInternal(next);
        onValueChange?.(next);
    };
    const clear = () => {
        setValue('');
        runSearch('', true);
        onClear?.();
        inputRef.current?.focus();
    };
    useEffect(() => {
        if (!shortcutKeys)
            return;
        const combos = shortcutKeys.split(' ').filter(Boolean);
        const onKey = (e) => {
            if (e.defaultPrevented)
                return;
            const combo = combos.find((c) => matchesShortcut(e, c));
            if (!combo)
                return;
            const plain = !/(control|ctrl|meta|alt)\+/i.test(combo);
            if (plain && isEditable(e.target))
                return;
            const input = inputRef.current;
            if (!input || input.disabled)
                return;
            e.preventDefault();
            input.focus();
            input.select();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [shortcutKeys]);
    const handleKey = (e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented)
            return;
        if (e.key === 'Enter') {
            runSearch(value, true);
        }
        else if (e.key === 'Escape' && value !== '') {
            // Escape clears first; a second Escape (empty field) reaches dialogs and popovers.
            e.preventDefault();
            e.stopPropagation();
            clear();
        }
    };
    const fieldProps = useFieldControl(rest, { invalid });
    const showClear = clearable && value !== '' && !fieldProps.disabled && !rest.readOnly;
    const leading = pending ? _jsx(Spinner, { size: "sm" }) : icon === undefined ? _jsx(SearchGlyph, {}) : icon;
    return (_jsxs("div", { className: cx('mn-input', 'mn-search-input', className), "data-size": size, "data-pending": pending || undefined, "data-disabled": fieldProps.disabled || undefined, "data-invalid": fieldProps['aria-invalid'] ? true : undefined, children: [leading ? (_jsx("span", { className: "mn-search-input__icon", "aria-hidden": true, children: leading })) : null, _jsx("input", { ref: (node) => {
                    inputRef.current = node;
                    if (typeof ref === 'function')
                        ref(node);
                    else if (ref)
                        ref.current = node;
                }, autoComplete: "off", spellCheck: false, enterKeyHint: "search", ...fieldProps, type: "search", "aria-keyshortcuts": shortcutKeys || undefined, "aria-busy": pending || undefined, className: "mn-search-input__input", value: value, onChange: (e) => {
                    onChange?.(e);
                    setValue(e.target.value);
                    runSearch(e.target.value, false);
                }, onKeyDown: handleKey }), showClear ? (_jsx("button", { type: "button", className: "mn-search-input__clear", "aria-label": clearLabel, onMouseDown: (e) => e.preventDefault(), onClick: clear, children: _jsx(CloseGlyph, {}) })) : shortcut && value === '' ? (_jsx(Kbd, { className: "mn-search-input__shortcut", "aria-hidden": true, children: shortcut })) : null] }));
});
//# sourceMappingURL=search-input.js.map